import {
    RisksRates,
    SuppliersStatuses,
    TasksStatuses,
} from "src/modules/3rd-party-management/constants";
import { formatDisplayValue, formatPersonName, normalizeHexColor } from "./index";

const resolveRiskRating = (supplierDetail = {}, overview = {}) => {
    if (supplierDetail?.highestRisk?.title) {
        return {
            title: supplierDetail.highestRisk.title,
            color: normalizeHexColor(`#${supplierDetail.highestRisk.color || "822626"}`, "#822626"),
        };
    }

    const overviewRisk = overview?.highestRisk;

    if (overviewRisk) {
        const normalizedColor = normalizeHexColor(
            overviewRisk.startsWith("#") ? overviewRisk : `#${overviewRisk}`,
            "#822626"
        );
        const matchedRate = Object.values(RisksRates).find(
            (rate) => rate.color.toLowerCase() === normalizedColor.toLowerCase()
        );

        return {
            title: matchedRate?.title || "-",
            color: normalizedColor,
        };
    }

    return {
        title: "-",
        color: "#822626",
    };
};

const TASK_STATUS_KEYS = ["NEW", "IN_PROGRESS", "REVIEW", "CLOSED", "DELETED"];

const TASK_STATUS_API_KEYS = {
    NEW: "NEW",
    IN_PROGRESS: "IN_PROGRESS",
    REVIEW: "REVIEW",
    CLOSED: "CLOSE",
    DELETED: "DELETE",
};

const resolvePercentValue = (...values) => {
    for (const value of values) {
        if (value === null || value === undefined || value === "") {
            continue;
        }

        const parsedValue = Number(value);

        if (!Number.isNaN(parsedValue)) {
            return parsedValue;
        }
    }

    return 0;
};

const isClosedTaskStatus = (status = "") => {
    const normalizedStatus = `${status}`.toUpperCase();

    return normalizedStatus === "CLOSED" || normalizedStatus === "CLOSE";
};

const computeMitigatedRisksPercent = (tasks = []) => {
    const mitigationTasks = (Array.isArray(tasks) ? tasks : []).filter(
        (task) => task?.is_mitigation
    );

    if (!mitigationTasks.length) {
        return null;
    }

    const closedMitigationTasks = mitigationTasks.filter((task) =>
        isClosedTaskStatus(task?.status)
    ).length;

    return Math.round((closedMitigationTasks / mitigationTasks.length) * 100);
};

const getRiskTitleByColor = (color = "") => {
    const normalizedColor = normalizeHexColor(
        color.startsWith("#") ? color : `#${color}`,
        "#CCCCCC"
    );

    const matchedRate = Object.values(RisksRates).find(
        (rate) => normalizeHexColor(rate.color, "#CCCCCC").toLowerCase() === normalizedColor.toLowerCase()
    );

    return matchedRate?.title || "Other";
};

const getSupplierStatusLabel = (status, t) => {
    if (status === null || status === undefined || status === "") {
        return "-";
    }

    if (typeof status === "number" || `${status}`.match(/^\d+$/)) {
        return t(SuppliersStatuses[status]?.title || "-");
    }

    const normalizedStatus = `${status}`.toLowerCase();

    const matchedStatus = Object.values(SuppliersStatuses).find(
        (item) => item.title.toLowerCase() === normalizedStatus
    );

    return matchedStatus ? t(matchedStatus.title) : t(status);
};

const buildRiskLevelItems = (statistics = {}, supplierDetail = {}, risks = [], riskStatistics = {}) => {
    if (riskStatistics && Object.keys(riskStatistics).length) {
        return Object.keys(riskStatistics)
            .map((key) => ({
                title: riskStatistics[key]?.title,
                amount: Number(riskStatistics[key]?.amount || 0),
                color: normalizeHexColor(`#${riskStatistics[key]?.color || "CCCCCC"}`, "#CCCCCC"),
            }))
            .filter((item) => item.title);
    }

    const riskRate = statistics?.riskRate || {};
    const items = Object.keys(riskRate).map((key) => ({
        title: riskRate[key]?.title,
        amount: Number(riskRate[key]?.amount || 0),
        color: normalizeHexColor(`#${riskRate[key]?.color || "CCCCCC"}`, "#CCCCCC"),
    })).filter((item) => item.title);

    if (items.some((item) => item.amount > 0)) {
        return items;
    }

    if (Array.isArray(risks) && risks.length) {
        const groupedByColor = risks.reduce((acc, risk) => {
            const color = normalizeHexColor(`#${risk?.result_color || "CCCCCC"}`, "#CCCCCC");
            acc[color] = (acc[color] || 0) + 1;
            return acc;
        }, {});

        return Object.keys(groupedByColor).map((color) => ({
            title: getRiskTitleByColor(color),
            amount: Number(groupedByColor[color] || 0),
            color,
        }));
    }

    if (supplierDetail?.highestRisk?.title) {
        return [{
            title: supplierDetail.highestRisk.title,
            amount: 1,
            color: normalizeHexColor(`#${supplierDetail.highestRisk?.color || "822626"}`, "#822626"),
        }];
    }

    return [];
};

const buildTaskItems = (statistics = {}, tasks = [], taskStatistics = {}) => {
    if (taskStatistics && Object.keys(taskStatistics).length) {
        return TASK_STATUS_KEYS.map((statusKey) => {
            const apiKey = TASK_STATUS_API_KEYS[statusKey];
            const stat = taskStatistics[apiKey];

            return {
                title: TasksStatuses[statusKey]?.title,
                amount: Number(stat?.amount || 0),
                color: TasksStatuses[statusKey]?.color || "#CCCCCC",
            };
        }).filter((item) => item.title);
    }

    const taskStats = statistics?.task?.data;

    if (taskStats && Object.keys(taskStats).length) {
        return TASK_STATUS_KEYS.map((statusKey) => ({
            title: TasksStatuses[statusKey]?.title,
            amount: Number(taskStats[statusKey] || 0),
            color: TasksStatuses[statusKey]?.color || "#CCCCCC",
        })).filter((item) => item.title);
    }

    const grouped = tasks.reduce((acc, task) => {
        const statusKey = (task?.status || "").toUpperCase();
        acc[statusKey] = (acc[statusKey] || 0) + 1;
        return acc;
    }, {});

    return TASK_STATUS_KEYS.map((statusKey) => ({
        title: TasksStatuses[statusKey]?.title,
        amount: Number(grouped[statusKey] || 0),
        color: TasksStatuses[statusKey]?.color || "#CCCCCC",
    })).filter((item) => item.title);
};

const buildRecentActivities = (lastActivities = [], news = []) => {
    if (Array.isArray(lastActivities) && lastActivities.length) {
        return lastActivities;
    }

    return (Array.isArray(news) ? news : [])
        .slice(0, 5)
        .map((item) => ({
            id: item?.id,
            created_at: item?.created_at
                ? Number(item.created_at) * 1000
                : (item?.release_date?.timestamp ? Number(item.release_date.timestamp) * 1000 : null),
            text: item?.title || item?.keyword || "Update available",
        }))
        .filter((item) => item.created_at || item.text);
};

const DAMAGE_LEVELS = [1000, 10000, 100000, 500000, 1000000];
const LIKELIHOOD_LEVELS = [90, 75, 50, 25];
const CELL_FALLBACK_COLORS = ["4EB8B1", "59C94F", "FFA049", "FF4949", "822626"];

const resolveLikelihoodPercent = (risk = {}) => {
    const likelihoodId = Number(risk?.likelihood?.id || 0);

    if (likelihoodId >= 4) return 90;
    if (likelihoodId === 3) return 75;
    if (likelihoodId === 2) return 50;
    return 25;
};

const resolveDamageIndex = (risk = {}) => {
    const damageValue = Number(risk?.damage?.title || 0);

    for (let index = DAMAGE_LEVELS.length - 1; index >= 0; index -= 1) {
        if (damageValue >= DAMAGE_LEVELS[index]) {
            return index;
        }
    }

    return 0;
};

const buildRiskMatrixFallback = (riskMatrixData = null, risks = []) => {
    if (riskMatrixData?.matrix?.length) {
        return riskMatrixData;
    }

    if (!Array.isArray(risks) || !risks.length) {
        return null;
    }

    const damageLevels = DAMAGE_LEVELS;
    const likelihoodLevels = LIKELIHOOD_LEVELS;

    const likelihoods = likelihoodLevels.map((label, index) => ({
        id: index + 1,
        label,
    }));

    const consequences = damageLevels.map((title, index) => ({
        id: index + 1,
        title: `${title}`,
    }));

    const matrix = likelihoodLevels.map((likelihoodLabel, rowIndex) => {
        return damageLevels.map((damageThreshold, colIndex) => {
            const cellRisks = risks.filter((risk) => {
                const likelihoodValue = resolveLikelihoodPercent(risk);
                const damageIndex = resolveDamageIndex(risk);

                return likelihoodValue === likelihoodLabel && damageIndex === colIndex;
            });

            const color = cellRisks[0]?.result_color || CELL_FALLBACK_COLORS[colIndex] || "FFFFFF";

            return {
                id: `${rowIndex}-${colIndex}`,
                color,
                risks: cellRisks,
            };
        });
    });

    return {
        matrix,
        consequences,
        likelihoods,
    };
};

const buildManagementSummaryData = ({
    supplierData = {},
    overviewHeader = {},
    statistics = {},
    lastActivities = [],
    riskMatrixData = null,
    dateUtils,
    t = (value) => value,
    isOwnCompany = false,
}) => {
    const supplierDetail = supplierData?.supplierDetail || {};
    const overview = overviewHeader || {};
    const taskStatistics = supplierData?.taskStatistics || statistics?.taskStatistics || {};
    const riskStatistics = supplierData?.riskStatistics || statistics?.riskStatistics || {};

    const createdOn = overview.createdAt || supplierDetail.creationDate;
    const lastUpdate = overview.updatedAt || supplierDetail.lastUpdate || supplierDetail.updateDate;
    const statusValue = overview.status ?? supplierDetail.status;
    const riskRating = resolveRiskRating(supplierDetail, overview);
    const mitigatedRisksPercent = resolvePercentValue(
        supplierData?.completedMitigatedTasks,
        overview.completedMitigatedTasks,
        supplierDetail.completedMitigatedTasks,
        statistics?.completedMitigatedTasks,
        computeMitigatedRisksPercent(supplierData?.tasks),
    );
    const completedTasksPercent = resolvePercentValue(
        supplierData?.completedTasks,
        overview.completedTasks,
        supplierDetail.completedTasks,
        statistics?.completedTasks,
        taskStatistics?.CLOSE?.percent,
        taskStatistics?.CLOSED?.percent,
    );

    return {
        firstRow: [
            {
                title: "ID",
                value: formatDisplayValue(overview.secondaryId ?? supplierDetail.secondaryId),
            },
            {
                title: "Name",
                value: formatDisplayValue(overview.name ?? supplierDetail.name),
            },
            {
                title: isOwnCompany || supplierDetail.type === "individual" ? "Nickname / AKA" : "Short Name",
                value: formatDisplayValue(overview.shortName ?? supplierDetail.shortName),
            },
            {
                title: "Created on",
                value: createdOn
                    ? dateUtils.convertDateToDate(createdOn, "DD/MM/YYYY")
                    : "-",
            },
            {
                title: "Last Update",
                value: lastUpdate
                    ? dateUtils.convertDateToDate(lastUpdate, "DD/MM/YYYY")
                    : "-",
            },
            {
                title: "Risk Rating",
                value: riskRating.title,
                riskColor: riskRating.color,
            },
        ],
        secondRow: [
            {
                title: "Status",
                type: "text",
                value: getSupplierStatusLabel(statusValue, t),
            },
            {
                title: "Assigned Analyst",
                type: "text",
                value: formatPersonName(
                    overview.analystAdmin || supplierDetail.assignedAnalyst
                ),
            },
            {
                title: "Mitigated Risks",
                type: "percent",
                value: mitigatedRisksPercent,
            },
            {
                title: "Completed Tasks",
                type: "percent",
                value: completedTasksPercent,
            },
        ],
        riskLevels: buildRiskLevelItems(statistics, supplierDetail, supplierData?.risks || [], riskStatistics),
        tasks: buildTaskItems(statistics, supplierData?.tasks || [], taskStatistics),
        recentActivities: buildRecentActivities(lastActivities, supplierData?.news || []),
        riskMatrixData: buildRiskMatrixFallback(riskMatrixData, supplierData?.risks || []),
    };
};

export {
    buildManagementSummaryData,
    getSupplierStatusLabel,
    getRiskTitleByColor,
};
